import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polling, setPolling] = useState(true);
  const { refreshMe } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid") {
          setPolling(false);
          await refreshMe();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired" || attempts > 10) {
          setPolling(false);
          return;
        }
      } catch {}
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refreshMe]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "expired";

  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
      <div className="w-full max-w-lg rounded-3xl border border-border bg-card p-10 text-center">
        {polling && !paid && !failed && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-2xl font-black">Confirming payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Hold tight, Stripe is verifying your subscription.</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-accent" />
            <h1 className="mt-4 font-display text-3xl font-black tracking-tighter">You're in!</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your {status.plan?.replace("_", " ") || "Pro"} plan is active. Unlimited mentorship unlocked.</p>
            <Button className="mt-6 rounded-full btn-neon font-mono text-xs uppercase tracking-widest" onClick={() => navigate("/app/dashboard")}>
              Enter LaneX <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}
        {failed && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 font-display text-2xl font-black">Payment didn't go through</h1>
            <p className="mt-2 text-sm text-muted-foreground">No charge was made. You can try again anytime.</p>
            <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/pricing")}>Back to pricing</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
      <div className="w-full max-w-lg rounded-3xl border border-border bg-card p-10 text-center">
        <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 font-display text-2xl font-black">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-muted-foreground">No worries — your free access continues unchanged.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/pricing")}>See plans</Button>
          <Button className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" onClick={() => navigate("/app/dashboard")}>Back to app</Button>
        </div>
      </div>
    </div>
  );
}
